import { useState } from "react";
import { Loader2, Mail } from "lucide-react";
import { recoverTrackers } from "@/lib/api";
import { cn } from "@/lib/utils";

type FormState = "idle" | "submitting" | "done" | "error";

interface Props {
  variant?: "default" | "hero";
  id?: string;
  className?: string;
}

const TrackerRecoveryForm = ({ variant = "default", id = "tracker-recovery", className }: Props) => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<FormState>("idle");

  const inHero = variant === "hero";
  const chromeClass = inHero
    ? "rounded-2xl border border-primary/30 bg-primary/5 p-6"
    : "bg-card rounded-2xl border border-border shadow-sm p-5";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setState("submitting");
    try {
      await recoverTrackers(email.trim());
      setState("done");
    } catch {
      setState("error");
    }
  };

  const handleSendAgain = () => {
    setEmail("");
    setState("idle");
  };

  if (state === "done") {
    return (
      <div
        id={id}
        className={cn(chromeClass, className)}
        aria-live="polite"
      >
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Mail className="w-4 h-4 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-bold text-foreground">Check your inbox</h3>
            <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
              If you have any orders linked to that email, we've sent your tracker links to your inbox.
            </p>
            <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
              Can't find it? Check your spam or promotions folder.
            </p>
            <button
              type="button"
              onClick={handleSendAgain}
              className="mt-3 text-sm font-semibold text-primary hover:underline"
            >
              Send again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const submitting = state === "submitting";

  return (
    <form
      id={id}
      onSubmit={handleSubmit}
      className={cn(chromeClass, className)}
    >
      {inHero ? (
        <>
          <h2 className="text-base font-bold text-foreground mb-1">Wondering about your Korban?</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Enter the email you used for your order. We'll send your tracker links where you can check your korban status any time.
          </p>
        </>
      ) : (
        <>
          <h3 className="text-sm font-bold text-foreground">Already ordered? Find your tracker</h3>
          <p className="text-xs text-muted-foreground mt-1 mb-3">
            Enter the email you used at checkout — we'll send your tracker links to your inbox.
          </p>
        </>
      )}
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Your email"
          className="flex-1 px-4 py-3 rounded-xl border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
          disabled={submitting}
        />
        <button
          type="submit"
          disabled={submitting || !email.trim()}
          className={cn(
            "btn-primary text-sm whitespace-nowrap",
            (submitting || !email.trim()) && "opacity-60 cursor-not-allowed",
          )}
        >
          {submitting ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Sending…
            </span>
          ) : (
            "Send tracker links"
          )}
        </button>
      </div>
      {state === "error" && (
        <p className="text-xs text-red-600 mt-3" role="alert">
          Something went wrong. Please try again.
        </p>
      )}
    </form>
  );
};

export default TrackerRecoveryForm;
